import React, { useState, useEffect } from "react";
import { Tooltip, OverlayTrigger, Modal, Button, Form } from "react-bootstrap";
import { FaFileExcel, FaPhone, FaEnvelope } from "react-icons/fa";
import { TbHeartHandshake } from "react-icons/tb";
import "../Actividad.css";
import Sec from "../../../static/Logo-SEC.jpg";
import { getApiUrl } from "../../../utils/apiUrl";
import { getCsrfToken } from "../../../utils/getCsrfToken";

const DEFAULT_PROFILE_IMAGE =
  "https://cdn-redmaestra-endpoint.azureedge.net/static/images/default.png";

const getParticipantKey = (participant) =>
  `${participant.tipo_perfil}:${participant.rut}`;

function ActividadInfoForm({
  actividad,
  onActividadUpdated,
  apiBasePath = "api/perfiles/actividades",
}) {
  const [showTooltip, setShowTooltip] = useState(null);
  const [actividadLocal, setActividadLocal] = useState(actividad);
  const [showAprobacionesModal, setShowAprobacionesModal] = useState(false);
  const [showPagosModal, setShowPagosModal] = useState(false);
  const [approvedRutsDraft, setApprovedRutsDraft] = useState(new Set());
  const [paidRutsDraft, setPaidRutsDraft] = useState(new Set());
  const [savingAprobaciones, setSavingAprobaciones] = useState(false);
  const [savingPagos, setSavingPagos] = useState(false);
  const [aprobacionesError, setAprobacionesError] = useState(null);
  const [pagosError, setPagosError] = useState(null);

  // Keep local actividad synced if parent prop changes
  useEffect(() => {
    setActividadLocal(actividad);
  }, [actividad]);

  if (!actividad) {
    return <div>Cargando...</div>;
  }

  const alumnas = Array.isArray(actividadLocal?.elegidas)
    ? actividadLocal.elegidas
    : [];
  const totalAlumnas = alumnas.length;
  const totalInteresadas = alumnas.filter(
    (alumna) => alumna.tipo_perfil === "Interesada"
  ).length;
  const totalAprobadas = alumnas.filter((a) => a?.aprobada === "green").length;
  const totalRechazadas = alumnas.filter((a) => a?.aprobada === "red").length;
  const totalPagadas = alumnas.filter((a) => a?.pagada).length;

  const handleMouseEnter = (index) => {
    setShowTooltip(index);
  };

  const handleMouseLeave = () => {
    setShowTooltip(null);
  };

  const handleExportExcel = () => {
    if (actividadLocal?.id) {
      const exportUrl = getApiUrl(
        `${apiBasePath}/${actividadLocal.id}/excel/`
      );
      window.open(exportUrl, "_blank");
    }
  };

  // Open modal and preload current approved set from backend ("green" means approved)
  const openAprobacionesModal = () => {
    const initialApproved = new Set(
      alumnas
        .filter((a) => a?.aprobada === "green")
        .map(getParticipantKey)
        .filter(Boolean)
    );

    setApprovedRutsDraft(initialApproved);
    setAprobacionesError(null);
    setShowAprobacionesModal(true);
  };

  const closeAprobacionesModal = () => {
    if (savingAprobaciones) return;
    setShowAprobacionesModal(false);
  };

  const openPagosModal = () => {
    const initialPaid = new Set(
      alumnas
        .filter((a) => a?.pagada)
        .map(getParticipantKey)
        .filter(Boolean)
    );

    setPaidRutsDraft(initialPaid);
    setPagosError(null);
    setShowPagosModal(true);
  };

  const closePagosModal = () => {
    if (savingPagos) return;
    setShowPagosModal(false);
  };

  const toggleApprovedParticipant = (participant) => {
    const participantKey = getParticipantKey(participant);
    setApprovedRutsDraft((prev) => {
      const next = new Set(prev);
      if (next.has(participantKey)) next.delete(participantKey);
      else next.add(participantKey);
      return next;
    });
  };

  const togglePaidParticipant = (participant) => {
    const participantKey = getParticipantKey(participant);
    setPaidRutsDraft((prev) => {
      const next = new Set(prev);
      if (next.has(participantKey)) next.delete(participantKey);
      else next.add(participantKey);
      return next;
    });
  };

  // Save to backend
  const saveAprobaciones = async () => {
    if (!actividadLocal?.id) return;

    setSavingAprobaciones(true);
    setAprobacionesError(null);

    try {
      const csrfToken = await getCsrfToken();
      const url = getApiUrl(
        `${apiBasePath}/${actividadLocal.id}/aprobaciones/`
      );

      const resp = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        credentials: "include",
        body: JSON.stringify({
          approved_maestra_ruts: alumnas
            .filter(
              (a) =>
                a.tipo_perfil === "Maestra" &&
                approvedRutsDraft.has(getParticipantKey(a))
            )
            .map((a) => a.rut),
          approved_interesada_ruts: alumnas
            .filter(
              (a) =>
                a.tipo_perfil === "Interesada" &&
                approvedRutsDraft.has(getParticipantKey(a))
            )
            .map((a) => a.rut),
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        setAprobacionesError(data?.error || "Error saving approvals.");
        return;
      }

      // Backend returns the refreshed Actividad serializer payload
      setActividadLocal(data);
      if (typeof onActividadUpdated === "function") {
        onActividadUpdated(data); // you can pass the updated actividad payload
      }
      setShowAprobacionesModal(false);
    } catch (err) {
      setAprobacionesError("Network error saving approvals.");
    } finally {
      setSavingAprobaciones(false);
    }
  };

  const savePagos = async () => {
    if (!actividadLocal?.id) return;

    setSavingPagos(true);
    setPagosError(null);

    try {
      const csrfToken = await getCsrfToken();
      const url = getApiUrl(
        `${apiBasePath}/${actividadLocal.id}/pagos/`
      );

      const resp = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        credentials: "include",
        body: JSON.stringify({
          paid_maestra_ruts: alumnas
            .filter(
              (a) =>
                a.tipo_perfil === "Maestra" &&
                paidRutsDraft.has(getParticipantKey(a))
            )
            .map((a) => a.rut),
          paid_interesada_ruts: alumnas
            .filter(
              (a) =>
                a.tipo_perfil === "Interesada" &&
                paidRutsDraft.has(getParticipantKey(a))
            )
            .map((a) => a.rut),
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        setPagosError(data?.error || "Error guardando aportes registrados.");
        return;
      }

      setActividadLocal(data);
      if (typeof onActividadUpdated === "function") {
        onActividadUpdated(data);
      }
      setShowPagosModal(false);
    } catch (err) {
      setPagosError("Error de red guardando aportes registrados.");
    } finally {
      setSavingPagos(false);
    }
  };

  return (
    <div className="curso-info">
      <div className="curso-header">
        <h5>
          Alumnas
          <FaFileExcel
            title="Exportar a Excel"
            className="excel-icon"
            onClick={handleExportExcel}
            style={{ cursor: "pointer", marginLeft: "10px", color: "green" }}
          />
        </h5>

        <div className="alumnas-summary">
          <span className="summary-item">
            Total: <strong>{totalAlumnas}</strong>
          </span>

          <span className="summary-item">
            Interesadas: <strong>{totalInteresadas}</strong>
          </span>

          <span className="summary-item green-glow">
            A: <strong>{totalAprobadas}</strong>
          </span>

          <span className="summary-item red-glow">
            R: <strong>{totalRechazadas}</strong>
          </span>

          <span className="summary-item paid-glow">
            Aporte: <strong>{totalPagadas}</strong>
          </span>
        </div>

        <div className="alumnas-actions">
          <button
            type="button"
            className="alumnas-edit-btn"
            onClick={openAprobacionesModal}
          >
            Editar Aprobaciones
          </button>
          <button
            type="button"
            className="alumnas-edit-btn"
            onClick={openPagosModal}
          >
            Editar Aportes
          </button>
        </div>

      </div>
      <div className="curso-alumnas">
        {actividadLocal.elegidas && actividadLocal.elegidas.length > 0 ? (
          <div className="alumnas-container">
            {actividadLocal.elegidas.map((elegida, index) => {
              const esInteresada = elegida.tipo_perfil === "Interesada";
              const iniciaGrupoInteresadas =
                esInteresada &&
                (index === 0 ||
                  actividadLocal.elegidas[index - 1]?.tipo_perfil !==
                    "Interesada");

              return (
                <React.Fragment key={`${elegida.tipo_perfil}-${elegida.rut}`}>
                  {iniciaGrupoInteresadas && (
                    <div className="actividad-interesadas-divider">
                      <span>Interesadas</span>
                    </div>
                  )}
                  <div className="alumna-row">
                  {/* Column 1: Image with Tooltip */}
                  <OverlayTrigger
                    placement="top"
                    show={showTooltip === index}
                    overlay={
                      <Tooltip
                        id={`tooltip-${index}`}
                        className="custom-tooltip"
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <strong>{elegida.nombre}</strong>
                        <br />
                        {esInteresada ? (
                          <span>Interesada</span>
                        ) : (
                          <a href={`/todas/${elegida.rut}`}>Ver perfil</a>
                        )}
                      </Tooltip>
                    }
                  >
                    <div
                      className={`alumna-info ${elegida.aprobada ? `${elegida.aprobada}-glow` : ""}`}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <img
                        src={elegida.imagen || DEFAULT_PROFILE_IMAGE}
                        alt={elegida.nombre}
                        className="alumna-img"
                      />
                      {elegida.sec && (
                        <img
                          src={Sec}
                          alt="Certificación SEC"
                          className="actividad-alumna-sec-badge"
                        />
                      )}
                      {esInteresada && (
                        <span
                          className="actividad-interesada-avatar-badge"
                          title="Interesada"
                        >
                          I
                        </span>
                      )}
                      {elegida.pagada && (
                        <span
                          className="actividad-alumna-paid-badge"
                          title="Aporte registrado"
                        >
                          <TbHeartHandshake />
                        </span>
                      )}
                    </div>
                  </OverlayTrigger>

                  {elegida.comuna && (
                    <span className="alumna-comuna-tab">{elegida.comuna}</span>
                  )}

                  <div className="alumna-name">
                    <div className="alumna-name-line">
                      <span>{elegida.nombre}</span>
                      {elegida.celular && (
                        <a
                          href={`tel:${elegida.celular}`}
                          className="contact-icon-link"
                          title="Llamar"
                          aria-label={`Llamar a ${elegida.nombre}`}
                        >
                          <FaPhone />
                        </a>
                      )}
                      {elegida.correo && (
                        <a
                          href={`mailto:${elegida.correo}`}
                          className="contact-icon-link"
                          title="Enviar correo"
                          aria-label={`Enviar correo a ${elegida.nombre}`}
                        >
                          <FaEnvelope />
                        </a>
                      )}
                    </div>
                  </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div>No hay alumnas registradas</div>
        )}
      </div>
                                    {/* Aprobaciones Modal */}
      <Modal show={showAprobacionesModal} onHide={closeAprobacionesModal} centered>
        <Modal.Header closeButton={!savingAprobaciones}>
          <Modal.Title>Editar Aprobaciones</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {aprobacionesError && (
            <div className="aprobaciones-error">{aprobacionesError}</div>
          )}

          {alumnas.length === 0 ? (
            <div>No hay participantes registradas para aprobar</div>
          ) : (
            <div className="aprobaciones-list">
              {alumnas.map((a) => (
                <div
                  key={getParticipantKey(a)}
                  className={`aprobaciones-row ${
                    a.tipo_perfil === "Interesada"
                      ? "aprobaciones-row-interesada"
                      : ""
                  }`}
                >
                  <Form.Check
                    type="checkbox"
                    id={`aprob-${getParticipantKey(a)}`}
                    checked={approvedRutsDraft.has(getParticipantKey(a))}
                    onChange={() => toggleApprovedParticipant(a)}
                    disabled={savingAprobaciones}
                    label={
                      <span className="aprobaciones-label">
                        <strong>{a.nombre}</strong>
                        <span className="aprobaciones-sub">
                          
                          {a.tipo_perfil} {a.rut ? ` · ${a.rut}` : ""}
                        </span>
                      </span>
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeAprobacionesModal} disabled={savingAprobaciones}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveAprobaciones} disabled={savingAprobaciones}>
            {savingAprobaciones ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPagosModal} onHide={closePagosModal} centered>
        <Modal.Header closeButton={!savingPagos}>
          <Modal.Title>Editar Aportes Registrados</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {pagosError && (
            <div className="aprobaciones-error">{pagosError}</div>
          )}

          {alumnas.length === 0 ? (
            <div>No hay participantes registradas para editar aportes</div>
          ) : (
            <div className="aprobaciones-list">
              {alumnas.map((a) => (
                <div
                  key={getParticipantKey(a)}
                  className={`aprobaciones-row ${
                    a.tipo_perfil === "Interesada"
                      ? "aprobaciones-row-interesada"
                      : ""
                  }`}
                >
                  <Form.Check
                    type="checkbox"
                    id={`pago-${getParticipantKey(a)}`}
                    checked={paidRutsDraft.has(getParticipantKey(a))}
                    onChange={() => togglePaidParticipant(a)}
                    disabled={savingPagos}
                    label={
                      <span className="aprobaciones-label">
                        <strong>{a.nombre}</strong>
                        <span className="aprobaciones-sub">
                          {a.tipo_perfil} {a.rut ? ` · ${a.rut}` : ""}
                        </span>
                      </span>
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closePagosModal} disabled={savingPagos}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={savePagos} disabled={savingPagos}>
            {savingPagos ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ActividadInfoForm;
